import { useState } from 'react'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LocationsTable } from '@/components/locations/LocationsTable'
import { LocationWizard } from '@/components/locations/wizard/LocationWizard'
import { DeleteLocationDialog } from '@/components/locations/DeleteLocationDialog'
import { RecordStatusPill } from '@/components/data-table'
import { useLocations, useRestoreLocation } from '@/hooks/useLocations'
import { useLocationWizardStore } from '@/stores/locationWizardStore'
import type { RecordStatusFilter } from '@/types/record-status'

/**
 * Locations management page
 * Displays list of locations with CRUD operations
 */
export function LocationsPage() {
  // Wizard store (replaces local dialog state for create/edit)
  const { openCreate, openEdit } = useLocationWizardStore()

  // Record status filter (Active / All / Deleted)
  const [recordStatus, setRecordStatus] = useState<RecordStatusFilter>('active')

  // Delete dialog state (kept as local state)
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string
    name: string
  } | null>(null)

  // Fetch locations data
  const { data: locations, isLoading } = useLocations(recordStatus)
  const restoreLocation = useRestoreLocation()

  const handleEdit = (id: string) => {
    openEdit(id)
  }

  const handleDelete = (id: string, name: string) => {
    setDeleteTarget({ id, name })
  }

  const handleCloseDeleteDialog = (open: boolean) => {
    if (!open) {
      setDeleteTarget(null)
    }
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Locations</h1>
          <p className="text-muted-foreground">
            Manage your FBO locations and facilities
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RecordStatusPill value={recordStatus} onChange={setRecordStatus} />
          <Button onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Add Location
          </Button>
        </div>
      </div>

      {/* Locations Table */}
      <LocationsTable
        data={locations || []}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onRestore={(id) => restoreLocation.mutate(id)}
      />

      {/* Location Wizard (create/edit - reads isOpen from store) */}
      <LocationWizard />

      {/* Delete Confirmation Dialog */}
      {deleteTarget && (
        <DeleteLocationDialog
          open={!!deleteTarget}
          onOpenChange={handleCloseDeleteDialog}
          locationId={deleteTarget.id}
          locationName={deleteTarget.name}
        />
      )}
    </div>
  )
}
